// Question Generation observability — lists every question-authoring run
// (status, latency, attempts, errors), whatever seeded it. Reuses the async-jobs
// monitoring endpoint, filtered to the generation job types.
//
// BOTH job types belong here. They are one question to an admin — "where did
// these generated questions come from, and did it work" — and listing only the
// PDF one made every variations-from-questions run invisible.
// See docs/FeatureDesigns/async-jobs.md and question-variations-from-questions.md.
import { useMemo, useState, type MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOpenOperationRow } from './OperationNav';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';

const JOB_TYPES = ['generate-question-variations', 'generate-variations-from-questions'].join(',');

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  completed: 'success', failed: 'error', dead: 'error', running: 'warning', queued: 'default',
};

type Job = NonNullable<Awaited<ReturnType<typeof adminApi.getAsyncJobs>>>['items'][number];

export function GenerationList() {
  const [status, setStatus] = useState('');
  const openRow = useOpenOperationRow();

  const { data, isLoading, error } = useQuery({
    queryKey: ['generationJobs', status],
    queryFn: () => adminApi.getAsyncJobs({ type: JOB_TYPES, status: status || undefined, limit: 50 }),
  });

  const rows = data?.items ?? [];

  // Stop row navigation when an in-cell link is clicked (links open their own tab).
  const stop = (e: MouseEvent) => e.stopPropagation();

  const columns = useMemo<ColumnDef<Job>[]>(() => [
    { key: 'queued', header: 'Queued', render: (j) => format(new Date(j.queuedAt), 'MMM d, HH:mm:ss') },
    {
      key: 'teacher', header: 'Teacher', render: (j) => (
        <div>
          <div>{j.teacherName || '—'}</div>
          {j.teacherEmail && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{j.teacherEmail}</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (j) => <Badge variant={STATUS_BADGE[j.status] ?? 'default'} size="sm">{j.status}</Badge> },
    { key: 'attempts', header: 'Attempts', render: (j) => `${j.attempts}/${j.maxAttempts}` },
    { key: 'latency', header: 'Latency', render: (j) => (j.latencyMs != null ? `${(j.latencyMs / 1000).toFixed(1)}s` : '—') },
    {
      // What seeded the run — a PDF, a hand-picked selection, or a student's
      // mistakes. Without it two very different runs look identical in the list.
      key: 'source', header: 'Source', render: (j) => (
        <div>
          <div>{j.sourceLabel ?? '—'}</div>
          {j.sourcePdfUrl && (
            <a href={j.sourcePdfUrl} target="_blank" rel="noopener noreferrer" onClick={stop}>PDF ↗</a>
          )}
        </div>
      ),
    },
    {
      key: 'set', header: 'Assignment', render: (j) => (j.questionSetId
        ? <a href={`/question-sets/${j.questionSetId}`} target="_blank" rel="noopener noreferrer" onClick={stop}>set ↗</a>
        : '—'),
    },
    {
      key: 'questions', header: 'Questions', render: (j) => (j.generatedCount == null
        ? '—'
        : j.generatedCount > 0
          ? <a href={`/questions?batch=${j.id}&source=teacher`} target="_blank" rel="noopener noreferrer" onClick={stop}>{j.generatedCount} ↗</a>
          : '0'),
    },
    { key: 'error', header: 'Error', render: (j) => (j.lastError?.message ? <span className="dc-error" title={j.lastError.message}>{j.lastError.message.slice(0, 80)}</span> : '—') },
  ], []);

  const toolbar = (
    <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
      <option value="">All Statuses</option>
      <option value="completed">Completed</option>
      <option value="running">Running</option>
      <option value="queued">Queued</option>
      <option value="failed">Failed</option>
      <option value="dead">Dead</option>
    </select>
  );

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={(j) => j.id}
      onRowClick={(j) => openRow(rows, j, '/generation')}
      toolbar={toolbar}
      loading={isLoading}
      emptyMessage="No question-generation runs yet."
    />
  );
}
